'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import ProductImageFallback from '@/components/ui/ProductImageFallback';
import QuoteModal from '@/components/plc/QuoteModal';

interface Brand {
  name: string;
}

interface Category {
  id: string;
  name_en: string;
  name_ar: string;
  slug: string;
}

interface Product {
  id: string;
  mpn: string;
  description_en: string | null;
  stock_status: string | null;
  image_url: string | null;
  price: number | null;
  brands: Brand | null;
}

function StockBadge({ status }: { status: string | null }) {
  if (status === 'in_stock') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        In Stock
      </span>
    );
  }
  if (status === 'backorder') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
        Backorder
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/30 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
      <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
      Discontinued
    </span>
  );
}

export default function CategoryPage() {
  const params = useParams();
  const slug = typeof params.slug === 'string' ? params.slug : Array.isArray(params.slug) ? params.slug[0] : '';

  const [category, setCategory] = useState<Category | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteMpn, setQuoteMpn] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!slug) return;
    async function fetchData() {
      try {
        const supabase = createClient();

        // Fetch category by slug
        const { data: catData, error: catError } = await supabase
          .from('categories')
          .select('id, name_en, name_ar, slug')
          .eq('slug', slug)
          .single();

        if (catError || !catData) {
          setError('Category not found');
          setLoading(false);
          return;
        }

        setCategory(catData as Category);

        // Fetch products for this category by joining via category_id
        const { data: prodData, error: prodError } = await supabase
          .from('products')
          .select('id, mpn, description_en, stock_status, image_url, price, brands(name)')
          .eq('category_id', catData.id)
          .order('created_at', { ascending: false });

        if (prodError) throw prodError;
        setProducts((prodData as Product[]) ?? []);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [slug]);

  const handleAddToQuote = (e: React.MouseEvent, mpn: string) => {
    e.preventDefault();
    setQuoteMpn(mpn);
    setQuoteOpen(true);
  };

  return (
    <div className="min-h-screen bg-onyx text-white font-space-grotesk">
      {/* Page header */}
      <div className="border-b border-white/10 bg-black/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 pt-14">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-iraqi-red mb-2">
                Category
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">
                {loading ? 'Loading…' : category ? category.name_en : 'Category Not Found'}
              </h1>
              <p className="mt-2 text-white/50 text-sm">
                {loading
                  ? 'Loading catalog…'
                  : products.length > 0
                  ? `${products.length} product${products.length !== 1 ? 's' : ''} available`
                  : !error ? 'No products in this category yet' : ''}
              </p>
            </div>
            <Link
              href="/"
              className="text-sm text-white/40 hover:text-white/70 transition-colors whitespace-nowrap"
            >
              ← Back to home
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-24">
            <div className="flex flex-col items-center gap-4">
              <svg className="animate-spin h-8 w-8 text-iraqi-red" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              <p className="text-sm text-white/40">Loading products…</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="bg-iraqi-red/10 border border-iraqi-red/30 rounded-xl px-6 py-5 max-w-md">
              <p className="text-sm font-semibold text-red-400 mb-1">
                {error === 'Category not found' ? 'Category Not Found' : 'Failed to load products'}
              </p>
              <p className="text-xs text-white/40">{error}</p>
              <Link href="/" className="mt-3 inline-block text-xs text-iraqi-red hover:underline">
                ← Back to home
              </Link>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
              <svg className="w-8 h-8 text-white/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white/60 mb-2">No products found in this category yet</h2>
            <p className="text-sm text-white/30 max-w-xs">
              Check back later or{' '}
              <Link href="/products" className="text-iraqi-red hover:underline">
                browse all products
              </Link>
              .
            </p>
          </div>
        )}

        {/* Product grid */}
        {!loading && !error && products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map((product) => (
              <Link
                key={product.id}
                href={`/products/${encodeURIComponent(product.mpn)}`}
                className="group flex flex-col bg-white/[0.04] border border-white/10 rounded-2xl overflow-hidden hover:border-iraqi-red/40 hover:bg-white/[0.07] transition-all duration-300 hover:shadow-[0_8px_32px_rgba(206,17,38,0.12)]"
              >
                {/* Product image */}
                <div className="relative aspect-[4/3] bg-black/40 overflow-hidden">
                  <ProductImageFallback
                    src={product.image_url}
                    alt={`${product.mpn} — ${product.brands?.name ?? 'Industrial component'}`}
                    className="object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                  />
                  {product.brands?.name && (
                    <div className="absolute top-3 left-3">
                      <span className="text-[10px] font-bold uppercase tracking-wider bg-black/70 backdrop-blur-sm text-white/70 border border-white/10 px-2 py-0.5 rounded-md">
                        {product.brands.name}
                      </span>
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div className="flex flex-col flex-1 p-4 gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-0.5">MPN</p>
                    <p className="text-sm font-bold text-white font-mono leading-tight break-all">{product.mpn}</p>
                  </div>

                  {product.description_en && (
                    <p className="text-xs text-white/50 leading-relaxed line-clamp-2 flex-1">
                      {product.description_en}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-1 mt-auto">
                    <StockBadge status={product.stock_status} />
                    <button
                      onClick={(e) => handleAddToQuote(e, product.mpn)}
                      className="text-xs font-semibold text-iraqi-red hover:text-white hover:bg-iraqi-red px-2.5 py-1 rounded-lg border border-iraqi-red/40 transition-all duration-200"
                    >
                      + Quote
                    </button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {quoteOpen && (
        <QuoteModal
          isOpen={quoteOpen}
          onClose={() => setQuoteOpen(false)}
          language="en"
          prefillPartNumber={quoteMpn}
        />
      )}
    </div>
  );
}
